/**
 * Permission utilities for role-based access control
 */

export type UserRole =
  | 'ADMIN'
  | 'CASE_MANAGER'
  | 'CITIZEN'
  | 'TREASURY_ACCOUNT_CLERK'
  | 'TREASURY_OFFICER'
  | 'AUTHORIZING_OFFICER'
  | 'AUDITOR'
  | 'FIELD_OFFICER';

/**
 * Role matrix matching backend permissions
 */
export const roleMatrix = {
  VIEW_DASHBOARD: ['ADMIN', 'CASE_MANAGER', 'AUDITOR'],
  VIEW_CASE_DATA: ['ADMIN', 'CASE_MANAGER', 'AUDITOR'],
  VIEW_PROGRAMS: ['ADMIN', 'CASE_MANAGER', 'CITIZEN', 'AUDITOR'],
  VIEW_PARTICIPANTS: ['ADMIN', 'CASE_MANAGER', 'AUDITOR'],
  VIEW_PAYMENTS: ['ADMIN', 'CASE_MANAGER', 'TREASURY_ACCOUNT_CLERK', 'TREASURY_OFFICER', 'AUTHORIZING_OFFICER', 'AUDITOR'],
  MANAGE_USERS: ['ADMIN'],
  VIEW_AUDIT_LOGS: ['ADMIN', 'AUDITOR'],
} as const;

/**
 * Check if a user has a specific permission
 */
export function hasPermission(userRole: UserRole | undefined, permission: keyof typeof roleMatrix): boolean {
  if (!userRole) return false;
  return roleMatrix[permission].includes(userRole as 'ADMIN');
}

/**
 * Navigation item permissions mapping
 */
export const navPermissions = {
  '/': 'VIEW_DASHBOARD',
  '/cases': 'VIEW_CASE_DATA',
  '/applications': 'VIEW_CASE_DATA',
  '/tasks': 'VIEW_CASE_DATA',
  '/paysheets': 'VIEW_PAYMENTS',
  '/programs': 'VIEW_PROGRAMS',
  '/participants': 'VIEW_PARTICIPANTS',
  '/users': 'MANAGE_USERS',
  '/audit-logs': 'VIEW_AUDIT_LOGS',
} as const;

/**
 * Check if user can access a navigation path
 */
export function canAccessPath(userRole: UserRole | undefined, path: string): boolean {
  const permission = navPermissions[path as keyof typeof navPermissions];
  if (!permission) return true; // Allow access to unknown paths
  return hasPermission(userRole, permission);
}
